// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { bool, string, number, func, shape, node } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Button, RadioButtonGroup, Loading, RadioButton } from '@carbon/react';
import { ChevronUp } from 'lucide-react';

import usePermissions from 'Hooks/usePermissions';
import { StyledOverlay } from 'Components/Products/Products';

const CURRENCY_OPTIONS = {
  CORPORATE: [
    { label: 'EUR', value: 'eur', id: 'currency-eur' },
    { label: 'USD', value: 'usd', id: 'currency-usd' },
  ],
  STANDARD: [
    { label: 'LEI', value: 'ron', id: 'currency-ron' },
    { label: 'EUR', value: 'eur', id: 'currency-eur' },
    { label: 'USD', value: 'usd', id: 'currency-usd' },
  ],
};

const VAT_OPTIONS = [
  { labelKey: 'common.withVat', value: 1, id: 'vat-1' },
  { labelKey: 'common.withoutVat', value: 0, id: 'vat-0' },
];

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  svg {
    stroke: #707070 !important;
  }

  > div {
    position: relative;

    &:first-of-type {
      margin-right: 12px;
    }
  }

  @media screen and (max-width: ${props => props.theme.device.tablet}) {
    justify-content: space-around;
    width: 100%;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  outline: 0;
  border: 0;
  box-shadow: none !important;
  background-color: transparent !important;
  color: #707070 !important;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #525252 !important;
  }

  .currentValue {
    margin-left: 4px;
    font-weight: 700;
    border-bottom: 1px solid #707070;
    transition: border-color 0.2s ease;

    &.currency {
      text-transform: uppercase;
    }
  }

  &:hover .currentValue {
    border-bottom-color: #525252;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  width: 100%;
  min-width: 117px;
  padding: 1rem;
  top: 50px;
  left: 0;
  color: ${props => props.theme.colors.darkBlue};
  background-color: ${props => props.theme.colors.white};
  z-index: 13;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.25s ease-out, visibility 0.25s ease-out;
  border-radius: 4px;

  &::after {
    display: block;
    position: absolute;
    top: -5px;
    right: 1rem;
    width: 10px;
    height: 10px;
    background-color: ${props => props.theme.colors.white};
    transform: rotate(45deg);
    content: '';
  }

  .overlay {
    top: 0;
    left: 0;
    justify-content: center;
    align-items: center;
  }

  .cds--radio-button-group {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 6px;
  }

  @media screen and (max-width: ${props => props.theme.device.tablet}) {
    min-width: 104px;
  }
`;

const StyledChevronUp = styled(ChevronUp)`
  color: inherit;
  transform: scale(0.9) ${props => props.$menuOpen ? '' : 'rotate(180deg)'};
  margin-left: 8px;
  cursor: pointer;
  stroke: none !important;
`;

const formatCurrencyDisplay = currency => currency === 'ron' ? 'LEI' : currency.toUpperCase();

const DropdownButton = ({ label, currentValue, isOpen, onClick, isCurrency = false }) => (
  <StyledButton $isOpen={ isOpen } onClick={ onClick } kind="ghost">
    {label}: <span className={ `currentValue ${isCurrency ? 'currency' : ''}` }>{currentValue}</span>
    <StyledChevronUp $menuOpen={ isOpen } />
  </StyledButton>
);

DropdownButton.propTypes = {
  label: string.isRequired,
  currentValue: string.isRequired,
  isOpen: bool.isRequired,
  onClick: func.isRequired,
  isCurrency: bool,
};

const MenuDropdown = ({ isOpen, loading, children, theme }) => (
  <DropdownMenu theme={ theme } $isOpen={ isOpen }>
    <StyledOverlay className="overlay" open={ loading }>
      {loading && <Loading active withOverlay={ false } small />}
    </StyledOverlay>
    {children}
  </DropdownMenu>
);

MenuDropdown.propTypes = {
  isOpen: bool.isRequired,
  loading: bool.isRequired,
  children: node.isRequired,
  theme: shape({}).isRequired,
};

const SettingsMenu = forwardRef((props, ref) => {
  const {
    currencyOpen,
    vatOpen,
    loading,
    selectedCurrency,
    selectedVat,
    handleCurrencyChange,
    handleVatChange,
    onCurrencyToggle,
    onVatToggle,
  } = props;

  const theme = useTheme();
  const { t } = useTranslation();
  const { isExternalReseller } = usePermissions();
  const { currencyRef, vatRef } = ref || {};

  const currencyOptions = isExternalReseller ? CURRENCY_OPTIONS.CORPORATE : CURRENCY_OPTIONS.STANDARD;
  const showVatSection = !isExternalReseller;

  return (
    <StyledContainer>
      <div ref={ currencyRef } className="currencySection">
        <DropdownButton
          label={ t('common.currency') }
          currentValue={ formatCurrencyDisplay(selectedCurrency) }
          isOpen={ currencyOpen }
          onClick={ onCurrencyToggle }
          isCurrency
        />
        <MenuDropdown isOpen={ currencyOpen } loading={ loading } theme={ theme }>
          <RadioButtonGroup
            key={ `currency-${ selectedCurrency }-${ loading }` }
            name="defaultCurrency"
            valueSelected={ selectedCurrency }
            onChange={ handleCurrencyChange }
            legendText={ t('common.currency') }
            aria-label={ t('common.currency') }
          >
            {currencyOptions.map(option => (
              <RadioButton
                key={ option.id }
                labelText={ option.label }
                value={ option.value }
                id={ option.id }
              />
            ))}
          </RadioButtonGroup>
        </MenuDropdown>
      </div>

      {showVatSection && (
        <div ref={ vatRef } className="vatSection">
          <DropdownButton
            label={ t('common.prices') }
            currentValue={ selectedVat ? t('common.withVat') : t('common.withoutVat') }
            isOpen={ vatOpen }
            onClick={ onVatToggle }
          />
          <MenuDropdown isOpen={ vatOpen } loading={ loading } theme={ theme }>
            <RadioButtonGroup
              key={ `vat-${ selectedVat }-${ loading }` }
              name="withVat"
              valueSelected={ selectedVat }
              onChange={ handleVatChange }
              legendText={ t('common.prices') }
              aria-label={ t('common.prices') }
            >
              {VAT_OPTIONS.map(option => (
                <RadioButton
                  key={ option.id }
                  labelText={ t(option.labelKey) }
                  value={ option.value }
                  id={ option.id }
                />
              ))}
            </RadioButtonGroup>
          </MenuDropdown>
        </div>
      )}
    </StyledContainer>
  );
});

SettingsMenu.displayName = 'SettingsMenuComponent';

SettingsMenu.propTypes = {
  currencyOpen: bool,
  vatOpen: bool,
  loading: bool,
  selectedCurrency: string,
  selectedVat: number,
  handleCurrencyChange: func,
  handleVatChange: func,
  onCurrencyToggle: func,
  onVatToggle: func,
};

export default SettingsMenu;
